import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import ProgressMobileStepper from "../../../components/stepper/ProgressMobileStepper";
import SelectSiteStep from "./NewMemberDialogSteps/SelectSiteStep";
import MemberFormStep from "./NewMemberDialogSteps/MemberFormStep";
import ContractDataStep from "./NewMemberDialogSteps/ContractDataStep";
import SummaryStep from "./NewMemberDialogSteps/SummaryStep";
import FinishStep from "./NewMemberDialogSteps/FinishStep";
import SelectMemberTypeStep from "./NewMemberDialogSteps/SelectMemberTypeStep";
import SelectContractStep from "./NewMemberDialogSteps/SelectContractStep";
import { useAddNewMemberMutation } from "../../../features/members/membersApiSlice";
import Loading from "../../../components/spinners/Loading";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const steps = [
  "Wählen Sie einen Standort aus dem Sie ein Mitglied hinzufügen möchten:",
  "Welche Art von Mitglied möchten Sie anlegen?",
  "Wählen Sie aus welchem Vertrag Sie ein Mitglied hinzufügen möchten.",
  "Persönliche Daten des Mitglieds eingeben:",
  "Vertragsdaten",
  "Zusammenfassung / Daten prüfen",
  "Mitglied hinzugefügt",
];

function AddNewMemberDialog({ handleAskClose, handleClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [data, setData] = useState({
    site: null,
    memberType: null,
    addToContract: null,
    memberData: {},
    contractData: {},
    allowNext: false,
  });
  const [addnewMember, { isLoading }] = useAddNewMemberMutation();

  const handleReset = () => {
    setActiveStep(0);
    setData({
      site: null,
      memberType: null,
      addToContract: null,
      memberData: {},
      contractData: {},
      allowNext: false,
    });
  };

  const handleAllowNext = (allow) => {
    setData((prevData) => ({ ...prevData, allowNext: allow }));
  };

  const handleUploadNewMemberData = async () => {
    let result = await addnewMember(data);
    if (result?.data?.type === "SUCCESS") {
      console.log("Handle success");
    } else {
      // Error handling
      console.log("result :>> ", result);
    }
  };

  const handleNext = (upload) => {
    if (upload === "UPLOAD") {
      handleUploadNewMemberData();
      handleAllowNext(true);
      // Upload data, set Dialog to show loading
      // Create new Member, create new Contract or add to Contract, add member id to site a--> Backend stuffi
    }
    // Handle last Step:
    if (activeStep === steps.length - 1) {
      handleReset();
      handleClose();
    }

    // Normal step
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (data?.addToContract) {
      setActiveStep((prevActiveStep) => prevActiveStep - 2);
      return null;
    }

    // If memberType !== addMemberToContract => SelectContractStep needs to be skipped
    if (data.memberType !== "addMemberToContract" && activeStep === 3) {
      setActiveStep((prevActiveStep) => prevActiveStep - 2);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleSiteSelected = (site) => {
    setData((prevData) => ({ ...prevData, site }));
    handleNext();
  };

  const handleMemberTypeSelected = (memberType) => {
    setData((prevData) => ({ ...prevData, memberType }));
    if (memberType === "addMemberToContract") {
      handleNext();
    } else {
      // SelectContractStep needs to be skipped
      setActiveStep((prevActiveStep) => prevActiveStep + 2);
      setData((prevData) => ({ ...prevData, addToContract: false }));
    }
  };

  const handleMemberDataSubmit = (memberData) => {
    setData((prevData) => ({ ...prevData, memberData }));
    if (data?.addToContract) {
      // Skip contract step
      setActiveStep((prevActiveStep) => prevActiveStep + 2);
      return null;
    }
    handleNext();
  };

  const handleContractDataSubmit = (contractData) => {
    setData((prevData) => ({ ...prevData, contractData }));
    handleNext();
  };

  const handleNewMemberToExistingContractSubmit = (
    contractData,
    memberType
  ) => {
    setData((prevData) => ({
      ...prevData,
      contractData,
      memberType,
      addToContract: true,
    }));
    handleNext();
  };

  const stepsComponents = [
    <SelectSiteStep onSelect={handleSiteSelected} />,
    <SelectMemberTypeStep onSelect={handleMemberTypeSelected} />,
    <SelectContractStep onSubmit={handleNewMemberToExistingContractSubmit} />,
    <MemberFormStep
      memberData={data.memberData}
      siteData={data.site}
      onSubmit={handleMemberDataSubmit}
    />,
    <ContractDataStep
      memberType={data.memberType}
      memberData={data.memberData}
      contractData={data.contractData}
      onSubmit={handleContractDataSubmit}
    />,
    <SummaryStep data={data} handleNext={() => handleNext("UPLOAD")} />,
    <FinishStep data={data} handleReset={() => handleReset()} />,
  ];

  return (
    <Dialog
      open
      TransitionComponent={Transition}
      keepMounted
      maxWidth="lg"
      fullWidth
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogActions>
        <Button onClick={handleAskClose} color="inherit">
          <Icon width="40" icon="icon-park-solid:close-one" />
        </Button>
      </DialogActions>
      <DialogTitle
        style={{
          paddingTop: 0,
        }}
        variant="h4"
      >
        <Typography
          variant="h4"
          style={{
            borderBottomStyle: "solid",
            borderWidth: "2px",
            borderColor: "lightgrey",
            paddingTop: 0,
          }}
        >
          {steps[activeStep]}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <div style={{ minHeight: 300 }}>
            <Loading />
          </div>
        ) : (
          stepsComponents[activeStep]
        )}
      </DialogContent>
      <ProgressMobileStepper
        handleBack={() => handleBack()}
        handleNext={() => handleNext()}
        activeStep={activeStep}
        steps={steps.length}
        lastStep={activeStep === steps.length - 1}
        allowNext={data.allowNext}
      />
    </Dialog>
  );
}

AddNewMemberDialog.propTypes = {
  handleAskClose: PropTypes.func,
  handleClose: PropTypes.func,
};

export default AddNewMemberDialog;
